import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Dialog, DialogContent, useTheme, useMediaQuery } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import { AnimeTmdbImage } from '../../types/Anime';

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p';

interface AnimeGallerySectionProps {
  images: AnimeTmdbImage[];
}

const AnimeGallerySection: React.FC<AnimeGallerySectionProps> = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const thumbnailsRef = useRef<HTMLDivElement>(null);

  // backdrop + poster (backdropを優先表示)
  const backdrops = images.filter(i => i.image_type === 'backdrop');
  const posters = images.filter(i => i.image_type === 'poster');
  const allImages = [...backdrops, ...posters];

  const getImageUrl = (img: AnimeTmdbImage, size: 'thumb' | 'medium' | 'full') => {
    if (img.image_type === 'poster') {
      const sizeMap = { thumb: 'w342', medium: 'w500', full: 'w780' };
      return `${TMDB_IMAGE_BASE}/${sizeMap[size]}${img.file_path}`;
    }
    // backdrop: プレビューもダイアログもw780で十分
    const sizeMap = { thumb: 'w300', medium: 'w780', full: 'w1280' };
    return `${TMDB_IMAGE_BASE}/${sizeMap[size]}${img.file_path}`;
  };

  const scrollThumbnailIntoView = (index: number, containerId: string) => {
    const thumbnail = document.getElementById(`${containerId}-${index}`);
    if (thumbnail) {
      const container = thumbnail.parentElement;
      if (container) {
        const containerWidth = container.clientWidth;
        const thumbnailWidth = thumbnail.clientWidth;
        const scrollPosition = thumbnail.offsetLeft - (containerWidth / 2) + (thumbnailWidth / 2);
        container.scrollTo({ left: scrollPosition, behavior: 'smooth' });
      }
    }
  };

  useEffect(() => {
    scrollThumbnailIntoView(currentIndex, 'anime-thumb');
  }, [currentIndex]);

  useEffect(() => {
    if (dialogOpen) {
      scrollThumbnailIntoView(currentIndex, 'anime-dialog-thumb');
    }
  }, [currentIndex, dialogOpen]);

  const handlePrev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (currentIndex > 0) setCurrentIndex(prev => prev - 1);
  };

  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (currentIndex < allImages.length - 1) setCurrentIndex(prev => prev + 1);
  };

  if (allImages.length === 0) return null;

  const currentImage = allImages[currentIndex];

  return (
    <Box sx={{ mt: { xs: 2, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
          ギャラリー
        </Typography>
        <Box sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          color: 'white',
          fontSize: '0.7rem',
          padding: '2px 8px',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
        }}>
          {allImages.length}
        </Box>
      </Box>

      <Box sx={{ position: 'relative' }}>
        {/* Main Preview */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: currentImage.image_type === 'poster' ? '2/3' : '16/9',
            maxHeight: '400px',
            borderRadius: 1,
            overflow: 'hidden',
            cursor: 'pointer',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            '&:hover img': { opacity: 0.9 },
          }}
          onClick={() => setDialogOpen(true)}
        >
          <img
            src={getImageUrl(currentImage, 'medium')}
            alt=""
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              transition: 'opacity 0.2s',
            }}
          />
          <Box sx={{
            position: 'absolute',
            bottom: { xs: 4, sm: 8 },
            left: { xs: 4, sm: 8 },
            display: 'flex',
            gap: 0.5,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            padding: { xs: '2px 8px', sm: '4px 8px' },
            borderRadius: 1,
          }}>
            <Typography variant="caption" sx={{ color: 'white', fontSize: { xs: '0.5rem', sm: '0.7rem' } }}>
              {currentIndex + 1} / {allImages.length}
            </Typography>
          </Box>
        </Box>

        {/* Thumbnails */}
        <Box sx={{
          mt: 1,
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          pb: 1,
          '&::-webkit-scrollbar': { height: '6px' },
          '&::-webkit-scrollbar-track': { background: 'rgba(255, 255, 255, 0.05)', borderRadius: '3px' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '3px',
            '&:hover': { background: 'rgba(255, 255, 255, 0.25)' },
          },
        }}>
          {allImages.map((img, index) => (
            <Box
              key={img.id}
              id={`anime-thumb-${index}`}
              component="img"
              src={getImageUrl(img, 'thumb')}
              alt=""
              onClick={() => setCurrentIndex(index)}
              sx={{
                width: img.image_type === 'poster' ? '80px' : '120px',
                height: '67.5px',
                objectFit: 'cover',
                cursor: 'pointer',
                borderRadius: 1,
                flexShrink: 0,
                opacity: currentIndex === index ? 1 : 0.75,
                border: currentIndex === index ? '1px solid rgba(255, 255, 255, 0.5)' : 'none',
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 1 },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Full-screen Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth={false}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(12px)',
            overflow: 'hidden',
            width: { xs: '100%', sm: '1280px' },
            height: { xs: '100%', sm: '840px' },
            m: { xs: 0, sm: 2 },
          }
        }}
      >
        <IconButton
          onClick={() => setDialogOpen(false)}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: 'white',
            zIndex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{
          p: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&.MuiDialogContent-root': { overflow: 'hidden' },
        }}>
          {/* Main Image */}
          <Box sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 0,
            pt: { xs: 2, sm: 0 },
          }}>
            <Box
              component="img"
              src={getImageUrl(allImages[currentIndex], 'full')}
              alt=""
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                outline: 'none',
                userSelect: 'none',
              }}
            />
          </Box>

          {/* Bottom Thumbnails with Nav */}
          <Box sx={{
            mt: 'auto',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 1,
            pt: 2,
            pb: 2,
            height: '120px',
          }}>
            <IconButton
              onClick={handlePrev}
              disabled={currentIndex === 0}
              sx={{
                width: isMobile ? '32px' : '48px',
                height: '100px',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                opacity: currentIndex === 0 ? 0.5 : 1,
                borderRadius: 1,
                '&:hover': { backgroundColor: currentIndex === 0 ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)' },
              }}
            >
              <ArrowBackIosNewIcon sx={{ color: 'white' }} />
            </IconButton>

            <Box
              ref={thumbnailsRef}
              sx={{
                flex: 1,
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                userSelect: 'none',
                '& img': { userSelect: 'none' },
              }}
            >
              {allImages.map((img, index) => (
                <Box
                  key={img.id}
                  id={`anime-dialog-thumb-${index}`}
                  component="img"
                  src={getImageUrl(img, 'thumb')}
                  alt=""
                  onClick={() => setCurrentIndex(index)}
                  sx={{
                    width: isMobile ? '90px' : '160px',
                    height: isMobile ? '60px' : '90px',
                    objectFit: 'cover',
                    cursor: 'pointer',
                    borderRadius: 1,
                    flexShrink: 0,
                    opacity: currentIndex === index ? 1 : 0.75,
                    border: currentIndex === index ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
                    transition: 'opacity 0.2s',
                    '&:hover': { opacity: 1 },
                  }}
                />
              ))}
            </Box>

            <IconButton
              onClick={handleNext}
              disabled={currentIndex === allImages.length - 1}
              sx={{
                width: isMobile ? '32px' : '48px',
                height: '100px',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                opacity: currentIndex === allImages.length - 1 ? 0.5 : 1,
                borderRadius: 1,
                '&:hover': { backgroundColor: currentIndex === allImages.length - 1 ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)' },
              }}
            >
              <ArrowForwardIosIcon sx={{ color: 'white' }} />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AnimeGallerySection;
